'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Lesson, P, Pre, Quiz, Step } from '../_components/lesson'
import * as EX from './exercises/code-extra'

const CallTree = dynamic(() => import('../_components/widgets/CallTree'), { ssr: false })

export default function ClosuresAndRecursion() {
  return (
    <Lesson>
      <Step>
        <H>Functions that remember</H>
        <P>
          You know functions can be passed around like values. They can also be <strong>created</strong> by other functions. When
          that happens, the inner function keeps access to the variables around it — even after the outer function has finished.
          That “backpack” of remembered variables is called a <strong>closure</strong>.
        </P>
        <Exercise id="closures-intro" title="Closures in action" {...EX.closuresIntro} />
        <Callout kind="why">
          Your autograd engine in Module 7 depends on this. Every time it multiplies two numbers, it creates a little function that
          remembers <em>those two numbers</em>, so it can work out their gradients later. Thousands of tiny closures, each carrying
          its own memory.
        </Callout>
      </Step>

      <Step gate={['q-closure', 'closures']}>
        <H>Build some</H>
        <Quiz
          id="q-closure"
          question={
            <>
              Using <C>makeCounter</C> from above:
              <Pre>{`const a = makeCounter()
a()
a()
const b = makeCounter()
b()`}</Pre>
              What does that last <C>b()</C> return?
            </>
          }
          options={[
            { label: '1', correct: true },
            { label: '3', why: 'b has its own count — each call to makeCounter makes a fresh closure.' },
            { label: '0', why: 'count++ runs before the return, so the first call returns 1.' },
          ]}
        />
        <Exercise
          id="closures"
          title="compose and makeAccumulator"
          {...EX.closures}
          hints={[
            <>
              <C>{'return x => f(g(x))'}</C>
            </>,
            <>
              Declare <C>let sum = 0</C> inside <C>makeAccumulator</C>, then return an object whose <C>add</C> and <C>total</C>{' '}
              both use that same <C>sum</C>.
            </>,
          ]}
        />
      </Step>

      <Step>
        <H>Functions that call themselves</H>
        <P>
          Some problems contain smaller copies of themselves. The factorial of 5 is 5 times the factorial of 4. A folder contains
          folders. A computation graph contains smaller computation graphs. <strong>Recursion</strong> solves such problems with a
          function that calls itself on the smaller piece — until it reaches a piece so small it knows the answer outright (the{' '}
          <strong>base case</strong>).
        </P>
        <Exercise id="recursion-intro" title="Factorial and nested sums" {...EX.recursionIntro} />
      </Step>

      <Step gate="q-stack">
        <H>Watch the calls stack up</H>
        <P>
          The Fibonacci numbers are defined recursively: each is the sum of the two before it, <C>fib(n) = fib(n − 1) + fib(n − 2)</C>
          , with <C>fib(0) = 0</C> and <C>fib(1) = 1</C>. Step through the calls. The orange path is the <strong>call stack</strong>
          : functions that have started but are still waiting for an answer.
        </P>
        <Figure n="1.4" wide caption="The call tree of fib(n). Each call waits for its two children, then returns their sum. Notice how often the same small calls repeat.">
          <CallTree />
        </Figure>
        <Quiz
          id="q-stack"
          question="What happens if a recursive function has no base case?"
          options={[
            { label: 'It calls itself forever, until the call stack overflows and the program crashes', correct: true },
            { label: 'It returns 0', why: 'Nothing ever returns — each call just makes another call.' },
            { label: 'The computer works out the answer anyway', why: 'Without a stopping point there is no answer to work out.' },
          ]}
          explain="That crash is the famous “Maximum call stack size exceeded” error. Always ask: what’s my base case, and does every call move toward it?"
        />
      </Step>

      <Step gate="recursion">
        <H>Walking a tree</H>
        <P>
          Trees are where recursion is most natural. The last function here, <C>childrenFirst</C>, visits a node only after all
          of its children — exactly the order backpropagation needs, and the heart of the <C>backward()</C> method you’ll write.
        </P>
        <Exercise
          id="recursion"
          title="treeSum, depth, childrenFirst"
          {...EX.recursion}
          hints={[
            <>
              <C>{'node.value + node.children.reduce((s, c) => s + treeSum(c), 0)'}</C>
            </>,
            <>
              Depth: a leaf is 1; otherwise <C>{'1 + Math.max(...node.children.map(depth))'}</C>.
            </>,
            <>
              childrenFirst: collect the results for each child first (<C>{'out.push(...childrenFirst(c))'}</C>), then push the
              node’s own value.
            </>,
          ]}
        />
      </Step>
    </Lesson>
  )
}
